import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, DollarSign, PieChart, Users, Shield, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProfitAnalysisService } from '../../services/profitAnalysisService';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import ProfitFilters from './ProfitFilters';
import ProjectProfitTable from './ProjectProfitTable';
import TemplateProfitChart from './TemplateProfitChart';
import ClientProfitChart from './ClientProfitChart';
import PayrollAdmin from './PayrollAdmin';
import './ProfitAnalysis.css';

const ADMIN_EMAIL = 'adam@ab-civil.com';

const ProfitAnalysis = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Data state
  const [allProjects, setAllProjects] = useState([]);
  const [templateData, setTemplateData] = useState([]);
  const [clientData, setClientData] = useState([]);

  // Filter state - default to last 12 months
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    return {
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      templateTypes: [],
      clientIds: [],
      states: [],
      acreageRanges: [],
      billingStatuses: [],
      modelingStatuses: ['Completed'],
      searchTerm: ''
    };
  });

  // Chart colors (theme-aware)
  const [chartColors, setChartColors] = useState({
    textPrimary: '#F9FAFB',
    textSecondary: '#D1D5DB',
    borderPrimary: '#4B5563',
  });

  // Access check
  const hasAccess = user?.email === ADMIN_EMAIL;

  // Get computed CSS variables for chart colors
  const getChartColors = useCallback(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    return {
      textPrimary: rootStyles.getPropertyValue('--text-primary').trim() || '#F9FAFB',
      textSecondary: rootStyles.getPropertyValue('--text-secondary').trim() || '#D1D5DB',
      borderPrimary: rootStyles.getPropertyValue('--border-primary').trim() || '#4B5563',
    };
  }, []);

  // Update chart colors on theme change
  useEffect(() => {
    const updateColors = () => {
      setChartColors(getChartColors());
    };

    updateColors();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          updateColors();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, [getChartColors]);

  // Load data on mount and init console helpers
  useEffect(() => {
    if (hasAccess) {
      loadData();
      ProfitAnalysisService.initConsoleHelpers();
    }
  }, [hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch project data
      const projectsData = await ProfitAnalysisService.getProjectProfitData();

      setAllProjects(projectsData);

      // Calculate template and client profitability from project data
      const templateProfitData = ProfitAnalysisService.calculateTemplateProfitability(projectsData);
      const clientProfitData = ProfitAnalysisService.calculateClientProfitability(projectsData);

      setTemplateData(templateProfitData);
      setClientData(clientProfitData);
    } catch (err) {
      console.error('Error loading profit data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtered projects based on current filters
  const filteredProjects = useMemo(() => {
    return ProfitAnalysisService.filterProjects(allProjects, filters);
  }, [allProjects, filters]);

  // Filter options derived from all projects
  const filterOptions = useMemo(() => {
    return {
      templateTypes: ProfitAnalysisService.getUniqueTemplateTypes(allProjects),
      clients: ProfitAnalysisService.getUniqueClients(allProjects),
      states: ProfitAnalysisService.getUniqueStates(allProjects),
      billingStatuses: ProfitAnalysisService.getUniqueBillingStatuses(allProjects),
      modelingStatuses: ProfitAnalysisService.getUniqueModelingStatuses(allProjects)
    };
  }, [allProjects]);

  // Handler for filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Handler for clearing all filters (reset to last 12 months)
  const handleClearFilters = useCallback(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    setFilters({
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      templateTypes: [],
      clientIds: [],
      states: [],
      acreageRanges: [],
      billingStatuses: [],
      modelingStatuses: ['Completed'],
      searchTerm: ''
    });
  }, []);

  // Handler for CSV export
  const handleExport = useCallback(() => {
    ProfitAnalysisService.exportToCSV(filteredProjects);
  }, [filteredProjects]);

  // Access denied view
  if (!hasAccess) {
    return (
      <div className="profit-analysis-container">
        <div className="access-denied">
          <Shield size={64} />
          <h2>Access Denied</h2>
          <p>This page is restricted to authorized users only.</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="profit-analysis-container">
        <div className="loading-container">
          <LoadingSpinner />
          <p>Loading profit analysis data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="profit-analysis-container">
        <ErrorMessage
          message={error}
          onRetry={loadData}
        />
      </div>
    );
  }

  return (
    <div className="profit-analysis-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-title">
          <TrendingUp className="header-icon" size={28} />
          <h1>Profit Analysis</h1>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <DollarSign size={16} />
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'template' ? 'active' : ''}`}
            onClick={() => setActiveTab('template')}
          >
            <PieChart size={16} />
            By Template
          </button>
          <button
            className={`tab-button ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
          >
            <Users size={16} />
            By Client
          </button>
          <button
            className={`tab-button ${activeTab === 'labor' ? 'active' : ''}`}
            onClick={() => setActiveTab('labor')}
          >
            <Wallet size={16} />
            Labor Costs
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <>
            {/* Filters */}
            <ProfitFilters
              filters={filters}
              options={filterOptions}
              onChange={handleFilterChange}
              onClear={handleClearFilters}
              onExport={handleExport}
              projectCount={filteredProjects.length}
              totalCount={allProjects.length}
            />

            {/* Project Table */}
            <ProjectProfitTable
              projects={filteredProjects}
              chartColors={chartColors}
            />
          </>
        )}

        {activeTab === 'template' && (
          <TemplateProfitChart
            data={templateData}
            chartColors={chartColors}
          />
        )}

        {activeTab === 'client' && (
          <ClientProfitChart
            data={clientData}
            allProjects={allProjects}
            chartColors={chartColors}
          />
        )}

        {activeTab === 'labor' && (
          <PayrollAdmin onDataChange={loadData} />
        )}
      </div>
    </div>
  );
};

export default ProfitAnalysis;
